import { inject, Injectable } from '@angular/core';
import { BrowserStorageManagementService } from './browser-storage-management.service';

/** The specific key used to store the JWT in LocalStorage. */
const TOKEN_LOCALSTORAGE_KEY = 'Authorization';

/**
 * TokenService
 * * Abstraction layer for managing the user's authentication token.
 * Handles storage, retrieval, deletion, and parsing of the JWT.
 */
@Injectable({
  providedIn: 'root',
})
export class TokenService {
  private browserStorageManagementService = inject(BrowserStorageManagementService);

  /**
   * Checks if a token currently exists in storage.
   * @returns {boolean} True if token exists.
   */
  istokenExist(): boolean {
    return !!this.browserStorageManagementService.getLocalStorageItem(
      TOKEN_LOCALSTORAGE_KEY,
    );
  }

  /**
   * Retrieves the raw token string.
   * @returns {string} The JWT token.
   */
  getToken(): string {
    return this.browserStorageManagementService.getLocalStorageItem(
      TOKEN_LOCALSTORAGE_KEY,
    );
  }

  /**
   * Saves the access token to storage.
   * @param accessToken - The JWT string to save.
   */
  setToken(accessToken: string) {
    return this.browserStorageManagementService.setLocalStorageItem(
      TOKEN_LOCALSTORAGE_KEY,
      accessToken,
    );
  }

  /**
   * Removes the token from storage (Logout).
   */
  clearToken() {
    return this.browserStorageManagementService.removeLocalStorageItem(
      TOKEN_LOCALSTORAGE_KEY,
    );
  }

  /**
   * Decodes the JWT token to get the user ID.
   * Supports 'id' or 'userId' fields in the payload.
   * * @returns {string | null} The User ID or null if decoding fails.
   */
  getUserIdFromToken(): string | null {
    const token = this.getToken();
    if (!token) return null;

    try {
      // Simple JWT decode (Part 2 of the token)
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.id || payload.userId || null;
    } catch (e) {
      console.error('Error decoding token', e);
      return null;
    }
  }
}